import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  clearError,
  getShipmentGroups,
  deleteShipmentGroup,
} from "../../store/slices/shipmentGroupSlice";
import { getShipmentsForGroups } from "../../store/slices/shipmentSlice";
import { getVehicles } from "../../store/slices/vehiclesSlice";
import { getUsers } from "../../store/slices/userSlice";
import { Pagination } from "../../components/ListView/Pagination";
import { ShipmentGroupTable } from "../../components/groups/ShipmentGroupTable";
import ShipmentGroupModal from "../../components/groups/ShipmentGroupModal";
import { ShipmentGroup } from "../../types";
import Button from "../../components/UI/Button";
import { Input } from "../../components/UI/FormField";

export const ShipmentsGroupList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("shipmentGroup");

  const {
    groups,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.shipmentGroup);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">(
    "create",
  );
  const [selectedGroup, setSelectedGroup] = useState<ShipmentGroup | null>(
    null,
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [isBootstrapLoading, setIsBootstrapLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  /* ---------------- INITIAL BOOTSTRAP LOAD ---------------- */
  const bootstrapLoad = useCallback(async () => {
    try {
      setIsBootstrapLoading(true);

      await dispatch(getShipmentsForGroups({})).unwrap();
      await dispatch(getVehicles({})).unwrap();
      await dispatch(getUsers({ role: "driver" })).unwrap();

      await dispatch(
        getShipmentGroups({
          page: currentPage,
          limit: pageSize,
        }),
      ).unwrap();

      setIsReady(true);
    } catch (err) {
      toast.error(t("toast.loadFailed"));
    } finally {
      setIsBootstrapLoading(false);
    }
  }, [dispatch, currentPage, t]);

  useEffect(() => {
    bootstrapLoad();
  }, [bootstrapLoad]);

  /* ---------------- FILTER / SEARCH LOAD ---------------- */
  useEffect(() => {
    if (!isReady) return;

    dispatch(
      getShipmentGroups({
        page: currentPage,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
      }),
    );
  }, [dispatch, currentPage, searchTerm, isReady]);

  /* ---------------- ERROR HANDLING ---------------- */
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  /* ---------------- MODAL HANDLERS ---------------- */
  const handleOpenModal = (
    mode: "create" | "edit" | "view",
    group?: ShipmentGroup,
  ) => {
    if (!isReady) {
      toast.info(t("toast.dataStillLoading"));
      return;
    }

    setModalMode(mode);
    setSelectedGroup(group || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = async (isCancel: boolean) => {
    setIsModalOpen(false);
    setSelectedGroup(null);
    setModalMode("create");

    if (!isCancel) {
      setIsBootstrapLoading(true);
      await dispatch(getShipmentsForGroups({})).unwrap();
      await dispatch(
        getShipmentGroups({
          page: currentPage,
          limit: pageSize,
          ...(searchTerm && { search: searchTerm }),
        }),
      );
      setIsBootstrapLoading(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm(t("confirm.deleteGroup"))) return;

    try {
      await dispatch(deleteShipmentGroup(groupId)).unwrap();
      toast.success(t("toast.deleteSuccess"));

      dispatch(
        getShipmentGroups({
          page: currentPage,
          limit: pageSize,
        }),
      );
    } catch (err) {
      toast.error(err as string);
    }
  };

  /* ---------------- PAGINATION ---------------- */
  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div>
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">{t("page.title")}</h1>

        {/* Controls */}
        <div className="flex justify-between mb-4 gap-4 flex-wrap">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t("controls.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              disabled={isBootstrapLoading}
            />
          </div>

          <Button
            onClick={() => handleOpenModal("create")}
            variant="primary"
            disabled={isBootstrapLoading}
          >
            {t("controls.createGroup")}
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
          <ShipmentGroupTable
            groups={groups}
            onView={(group) => handleOpenModal("view", group)}
            onEdit={(group) => handleOpenModal("edit", group)}
            onDelete={handleDeleteGroup}
            loading={loading || isBootstrapLoading}
          />

          <Pagination
            pagination={pagination}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
          />
        </div>
      </div>

      {/* Modal */}
      <ShipmentGroupModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        group={selectedGroup}
      />
    </div>
  );
};
